import {useCallback, useEffect, useMemo} from 'react';

import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useLocation, useSearchParams} from 'react-router-dom';

import FuseSvgIcon from '@/@fuse/core/FuseSvgIcon';
import './styles';
import {PaymentLinkLogo} from '@/app/main/paymentLink/components/PaymentLinkLogo/PaymentLinkLogo';
import {PaymentLinkUtm} from '@/app/main/paymentLink/models/constants';
import PaymentLinkV2Promo from '@/app/main/paymentLink/v2/pages/paymentLinkV2/components/PaymentLinkV2Promo/PaymentLinkV2Promo';
import {
  CHECKOUT_RESPONSE_DATA,
  PAYMENT_LINK_V2_QUERY_PARAMS,
  SESSION_STORAGE_PAYMENT_DATA,
} from '@/app/main/paymentLink/v2/pages/paymentLinkV2/models/constants/constants';
import {PaymentLinkSuccessNavState} from '@/app/main/paymentLink/v2/pages/paymentLinkV2/models/interfaces/paymentLinkV2.interfaces';
import {AppButton, AppButtonVariant} from '@/app/shared-components';
import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import {PaymentLinkFlows} from '@/dataSource/src/api/payment-link/constants';
import {usePaymentLinkTrackers} from '@/dataSource/src/api/payment-link/trackers';
import {shortcodeToEmoji} from '@/utils/emojiShortcode';
import logError from '@/utils/errors/logError';
import {getFromStorage} from '@/utils/getFromStorage';

const DEFAULT_GOAL_EMOJI = '☃️';

const isUnresolvedShortcode = (value: string): boolean =>
  /^[:a-z0-9_+-]+$/i.test(value);

const getGoalEmoji = (icon?: string): string => {
  if (!icon) return '';
  const emoji = shortcodeToEmoji(icon);
  if (isUnresolvedShortcode(emoji)) return DEFAULT_GOAL_EMOJI;
  return emoji;
};

const PaymentLinkV2Success = () => {
  const {t} = useTranslation();
  const location = useLocation();
  const {
    trackViewRequestMoneySuccessScreen,
    trackSelectRequestMoneyShareInitiative,
  } = usePaymentLinkTrackers();
  const [searchParams] = useSearchParams();
  const state = (location.state as PaymentLinkSuccessNavState) || {};
  const paymentData: PaymentLinkSuccessNavState | null =
    state && state.paymentLinkId
      ? state
      : getFromStorage<PaymentLinkSuccessNavState>(
          SESSION_STORAGE_PAYMENT_DATA,
        );

  const paymentSessionId = searchParams.get(
    CHECKOUT_RESPONSE_DATA.PAYMENT_SESSION_ID,
  );
  const sessionId = searchParams.get(CHECKOUT_RESPONSE_DATA.SESSION_ID);
  const paymentId = searchParams.get(CHECKOUT_RESPONSE_DATA.PAYMENT_ID);

  const kidName = useMemo(
    () => paymentData?.kidName ?? '',
    [paymentData?.kidName],
  );

  const paymentLinkId = useMemo(
    () => paymentData?.paymentLinkId ?? '',
    [paymentData?.paymentLinkId],
  );

  const transactionId = useMemo(
    () => paymentData?.transactionId ?? '',
    [paymentData?.transactionId],
  );

  const receiverUserId = useMemo(
    () => paymentData?.receiverUserId ?? '',
    [paymentData?.receiverUserId],
  );

  const amount = useMemo(
    () => paymentData?.amount ?? '',
    [paymentData?.amount],
  );

  const flow = useMemo(
    () => paymentData?.flow ?? PaymentLinkFlows.QR,
    [paymentData?.flow],
  );

  const paymentMethod = useMemo(
    () => paymentData?.paymentMethod ?? '',
    [paymentData?.paymentMethod],
  );

  const cardType = useMemo(
    () => paymentData?.cardType ?? '',
    [paymentData?.cardType],
  );

  const isCampaign = useMemo(
    () =>
      searchParams.get(PAYMENT_LINK_V2_QUERY_PARAMS.FUND_RAISE) !== null ||
      Boolean(paymentData?.isCampaign),
    [searchParams, paymentData?.isCampaign],
  );

  const campaignTitle = useMemo(
    () => paymentData?.campaignTitle ?? '',
    [paymentData?.campaignTitle],
  );

  const isSavingsGoal = useMemo(
    () => Boolean(paymentData?.isSavingsGoal),
    [paymentData?.isSavingsGoal],
  );

  const goalName = useMemo(
    () => paymentData?.goalName ?? '',
    [paymentData?.goalName],
  );

  const goalIcon = useMemo(
    () => getGoalEmoji(paymentData?.goalIcon),
    [paymentData?.goalIcon],
  );

  const formattedAmount = useMemo(() => {
    if (typeof amount === 'number') return amount.toFixed(2);
    return amount;
  }, [amount]);

  const fundraiserShareUrl = useMemo(() => {
    if (!isCampaign || !paymentLinkId) return '';
    return `${window.location.origin}/payment-link/v2/${paymentLinkId}?${PAYMENT_LINK_V2_QUERY_PARAMS.FUND_RAISE}`;
  }, [isCampaign, paymentLinkId]);

  const handleShareFundraiser = useCallback(async () => {
    if (!fundraiserShareUrl) return;
    trackSelectRequestMoneyShareInitiative({
      id: paymentLinkId,
      type: paymentData?.linkType,
      campaignConfiguration: paymentData?.campaignConfiguration,
    });
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(fundraiserShareUrl);
        showSnackbar({
          summary: t('payment_link_v2.success.spread_the_word.copied'),
          variant: SnackbarType.INFO,
          message: '',
        });
      } else {
        logError(new Error('Clipboard API not supported'), {
          fileName: 'PaymentLinkV2SuccessPage.tsx',
          functionName: 'handleShareFundraiser',
        });
      }
    } catch (error) {
      logError(error, {
        fileName: 'PaymentLinkV2SuccessPage.tsx',
        functionName: 'handleShareFundraiser',
        customTitle: 'PaymentLinkV2 share fundraiser failed',
        extraTags: {
          payment_link_id: paymentLinkId,
        },
      });
    }
  }, [
    fundraiserShareUrl,
    paymentLinkId,
    paymentData?.linkType,
    paymentData?.campaignConfiguration,
    trackSelectRequestMoneyShareInitiative,
    t,
  ]);

  const trackViewSuccess = useCallback(() => {
    trackViewRequestMoneySuccessScreen({
      receiver_user_id: receiverUserId,
      request_money_id: paymentLinkId,
      flow: flow,
      payment_method: paymentMethod,
      transaction_id: transactionId,
      paymentSessionId: paymentSessionId,
      sessionId: sessionId,
      paymentId: paymentId,
      card_type: cardType,
    });
  }, [
    trackViewRequestMoneySuccessScreen,
    receiverUserId,
    paymentLinkId,
    flow,
    paymentMethod,
    transactionId,
    paymentSessionId,
    sessionId,
    paymentId,
    cardType,
  ]);

  useEffect(() => {
    trackViewSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="payment-link-v2-success-background">
      <Box className="payment-link-v2-success-container">
        <PaymentLinkLogo />

        <Box className="payment-link-v2-success-hero">
          <Box className="payment-link-v2-success-check">
            <FuseSvgIcon
              size={24}
              className="payment-link-v2-success-check-icon">
              heroicons-solid:check
            </FuseSvgIcon>
          </Box>

          {amount &&
            (isCampaign && campaignTitle ? (
              <Typography
                component="h1"
                className="payment-link-v2-success-title">
                {t('payment_link_v2.success.amount_sent_to_campaign', {
                  campaignTitle,
                  amount: formattedAmount,
                })}
              </Typography>
            ) : isSavingsGoal ? (
              <Box className="payment-link-v2-success-goal-info">
                <Typography
                  component="h1"
                  className="payment-link-v2-success-title">
                  {`${t('payment_link_v2.success.amount_sent_to_goal', {
                    amount: formattedAmount,
                    kidName,
                  })}\n${goalName}${goalIcon ? ` ${goalIcon}` : ''}`}
                </Typography>
                <Typography className="payment-link-v2-success-description">
                  {t('payment_link_v2.success.goal_description')}
                </Typography>
              </Box>
            ) : (
              kidName && (
                <Typography
                  component="h1"
                  className="payment-link-v2-success-title">
                  {t('payment_link_v2.success.amount_sent_to_name', {
                    kidName,
                    amount: formattedAmount,
                  })}
                </Typography>
              )
            ))}
        </Box>

        {isCampaign && (
          <Box className="payment-link-v2-success-spread">
            <Box
              component="img"
              src="/assets/images/etc/fundRaise3D.webp"
              alt="spread-the-word"
              className="payment-link-v2-success-spread-image"
            />
            <Box className="payment-link-v2-success-spread-content">
              <Box className="payment-link-v2-success-spread-text">
                <Typography
                  component="h2"
                  className="payment-link-v2-success-spread-title">
                  {t('payment_link_v2.success.spread_the_word.title')}
                </Typography>
                <Typography className="payment-link-v2-success-spread-description">
                  {t('payment_link_v2.success.spread_the_word.description')}
                </Typography>
              </Box>
              <AppButton
                id="payment-link-v2-success-share-fundraiser"
                label={t('payment_link_v2.success.spread_the_word.button')}
                variant={AppButtonVariant.onCheckout}
                onClick={handleShareFundraiser}
              />
            </Box>
          </Box>
        )}

        <PaymentLinkV2Promo
          utmCampaign={paymentData?.utmCampaign}
          utmMedium={PaymentLinkUtm.Medium.Success}
          isCampaign={isCampaign}
        />
      </Box>
    </section>
  );
};

export default PaymentLinkV2Success;
